import { useEffect, useMemo, useRef, useState } from 'react';
import { UPLOAD_SCENE } from '@/app/constants';
import { useStore, useStoreApi } from '@/app/providers/store';
import type { FormImagePrompt } from '@/app/types';
import { getMaxImageSize, isReferenceAssetSupported } from '@/app/utils';
import { cropImage, resizeImage, uploadImage } from '@/app/utils/client';

/**
 * 更新图片对象的prompt属性
 *
 * @param originalImage - 需要更新的原始图片对象
 * @param newImages - 包含新prompt信息的图片列表
 * @returns 如果在新列表中找到ID匹配的图片，则返回prompt属性已更新的图片对象；否则返回原始图片对象
 */
const mergePrompt = <T extends FormImagePrompt>(originalImage: T, newImages: (FormImagePrompt | null)[]): T => {
  const newImage = newImages.find((img) => img && img.id === originalImage.id);
  if (newImage) {
    return {
      ...originalImage,
      prompt: { ...originalImage.prompt, ...newImage.prompt },
    };
  }
  return originalImage;
};

/**
 * 监听分辨率选项变化并自动调整图片大小的 Hook
 *
 * 主要功能：
 * 1. 监听 resolution 选项的变化，计算新的 maxImageSize（提供的图片分辨率不需要大于视频分辨率，因此使用视频分辨率作为 maxImageSize）
 * 2. 当 maxImageSize 变化时，自动重新处理所有 selectedImage：
 *    - 如果图片有裁剪范围，先按裁剪范围重新截取图片
 *    - 将图片调整为符合新 maxImageSize 的尺寸
 *
 * @param type - 表单类型：'img2video' 或 'character2video' 或 'reference2image'
 * @returns {Object} 包含处理状态和当前最大图片尺寸
 *         - imageProcessing: 图片处理中状态
 *         - maxImageSize: 当前允许的最大图片尺寸
 */
export const useResizeByResolution = (type: 'img2video' | 'character2video' | 'reference2image') => {
  const [imageProcessing, setImageProcessing] = useState(false);
  const { modelVersion, formParams, updateFormParams, userInfo } = useStore((state) => ({
    modelVersion: state[`${type}FormParams`].modelVersion,
    formParams: state[`${type}FormParams`],
    updateFormParams: state.updateFormParams,
    userInfo: state.userInfo,
  }));
  const store = useStoreApi();
  const processingBatchIdRef = useRef(0);

  const maxImageSize = useMemo(() => getMaxImageSize(formParams), [formParams]);

  const isImagesInReferencePrompts = useRef(false);

  // 是否允许临时模式（未登录时不上传，只保存临时图片）
  const allowTempMode = true;

  function getImagePrompt(): (FormImagePrompt | null)[] {
    const formParams = store.getState()[`${type}FormParams`];

    if (isReferenceAssetSupported(modelVersion, type)) {
      isImagesInReferencePrompts.current = true;
      return (
        (formParams.referencePrompts
          ?.filter((item) => item.type === 'image' && item.prompt)
          .map(({ type, name, ...p }) => p) as FormImagePrompt[]) ?? []
      );
    }

    isImagesInReferencePrompts.current = false;
    return formParams.imagePrompts;
  }

  function updateImagePrompt(newImagePrompts: (FormImagePrompt | null)[]) {
    const formParams = store.getState()[`${type}FormParams`];

    // 根据条件更新不同的参数
    if (isImagesInReferencePrompts.current) {
      updateFormParams(
        type,
        'referencePrompts',
        formParams.referencePrompts?.map((p) => (p.type === 'image' ? mergePrompt(p, newImagePrompts) : p)),
      );
    } else {
      updateFormParams(
        type,
        'imagePrompts',
        formParams.imagePrompts?.map((p) => (p ? mergePrompt(p, newImagePrompts) : null)),
      );
    }
  }

  // 解决选择不同的分辨率时的处理
  useEffect(() => {
    const imagePrompts = getImagePrompt();
    if (imagePrompts.length === 0) return;

    async function processImages() {
      setImageProcessing(true);
      let hasUpdate = false;

      // 记录当前批次ID，用于判断是否是最新批次
      const currentBatchId = ++processingBatchIdRef.current;

      const res = await Promise.all(
        imagePrompts.map(async (item) => {
          if (!item?.prompt) return null;
          const { prompt } = item;

          // 如果有裁剪范围，需要先从原图上进行裁剪，再进行 resize
          const newSelectedImageUrl = prompt.selectedImageRange
            ? await cropImage(prompt.sourceImagePreview, prompt.selectedImageRange)
            : prompt.sourceImagePreview;
          if (!newSelectedImageUrl) return null;

          // 如果已经不是最新批次，不上传
          if (currentBatchId !== processingBatchIdRef.current) return { images: item, ifRevoke: false };

          const canvas = await resizeImage(newSelectedImageUrl, maxImageSize);
          if (!canvas) return null;

          // 如果重新裁剪后的图片尺寸和之前的尺寸完全一致，则不需要更新
          if (canvas.width === prompt.selectedImageWidth && canvas.height === prompt.selectedImageHeight) {
            return {
              images: item,
              ifRevoke: false,
            };
          }

          const imageResult = await uploadImage(canvas, 0.9, UPLOAD_SCENE.VIDU, allowTempMode, userInfo);
          if (!imageResult || imageResult.error) return null;
          const { error: _, ...image } = imageResult;

          prompt.selectedImagePreview && URL.revokeObjectURL(prompt.selectedImagePreview);
          hasUpdate = true;
          return {
            images: {
              ...item,
              prompt: {
                ...prompt,
                ...image,
              },
            },
            ifRevoke: true,
          };
        }),
      );

      // 只有当批次ID与最新的一致时，才更新状态
      if (currentBatchId === processingBatchIdRef.current) {
        setImageProcessing(false);
        hasUpdate && updateImagePrompt(res.map((item) => item?.images ?? null));
        return;
      }

      // 不是最新批次，释放资源
      res.forEach((item) => {
        if (item?.ifRevoke) {
          const imagePreview = item?.images?.prompt?.selectedImagePreview;
          if (imagePreview?.startsWith('blob:')) {
            URL.revokeObjectURL(imagePreview);
          }
        }
      });
    }

    // 如果处理图片出错，需要设置图片处理状态为 false
    try {
      processImages();
    } catch {
      setImageProcessing(false);
    }
  }, [maxImageSize]);

  return {
    imageProcessing,
    maxImageSize,
  };
};
